import { Box, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import ArrowLeft from "../../../common/icons/arrowLeft.svg";
import { useEffect, useState } from "react";
import { useAppDispatch, useAppSelector } from "../../../app/hooks";
import UnionFormBasicInfo from "./UnionFormSections/UnionFormBasicInfo";
import UnionTermsOfPaymentList from "./UnionFormSections/UnionTermsOfPaymentList";
import UnionBankAccount from "./UnionFormSections/UnionBankAccount";
import UnionLocalTradeUnions from "./UnionFormSections/UnionLocalTradeUnions";
import UnionNameAndNumber from "./UnionFormSections/UnionNameAndNumber";
import { Check as CheckIcon, Clear as DeleteIcon } from "@mui/icons-material";
import UnionPaymentInfo from "./UnionFormSections/UnionPaymentInfo/UnionPaymentInfo";
import { initialUnion, Union, UnionPayment } from "./unionsApi";
import {
  createTradeUnion,
  deleteTradeUnions,
  getUnionById,
  updateTradeUnion,
} from "./unionsSlice";
import { areObjectsEqual, normalizeInputValue } from "../../../app/common";
import { ConfirmationDialog } from "../../../common/components/functional/confirmationDialog/ConfirmationDialog";
import { CustomButton } from "../../../common/components/functional/customButton/CustomButton";

interface Props {
  selectedUnionId: string;
  setSelectedUnionId: (id: string) => void;
}

const UnionView = ({ selectedUnionId, setSelectedUnionId }: Props) => {
  const { t } = useTranslation();

  const dispatch = useAppDispatch();

  // Get union from redux adapter
  const storedUnion = useAppSelector(
    (state) => state.unions.unionDetails.entities[selectedUnionId],
  );

  const [union, setUnion] = useState<Union>({ ...initialUnion });
  const [deleteConfirmationDialogIsOpen, setDeleteConfirmationDialogIsOpen] =
    useState<boolean>(false);

  // Check if union is local union
  const isLocalUnion = selectedUnionId === "newLocal" || union?.parentId;

  const parentName = useAppSelector(
    (state) => state.unions.unionDetails.entities[union?.parentId || ""]?.name,
  );

  // If storedUnion is updated, update local state of union
  useEffect(() => {
    if (storedUnion) {
      setUnion({ ...storedUnion });
    }
  }, [storedUnion]);

  useEffect(() => {
    const currentTime = Date.now();
    const dataAge = currentTime - (storedUnion?.lastFetched || 0);
    const expireTime = 30 * 60 * 1000; // 30 minutes
    if (
      selectedUnionId !== "" &&
      selectedUnionId !== "new" &&
      selectedUnionId !== "newLocal" &&
      // If union is not stored in redux or it's older than 30 minutes
      (storedUnion === undefined || dataAge > expireTime)
    ) {
      /*
                Fetch union from API if it's new and 
                not already stored in redux
            */
      dispatch(
        getUnionById({
          unionId: selectedUnionId,
        }),
      );
    } else if (selectedUnionId === "newLocal") {
      // Set initial union with parentId from parent union
      setUnion({ ...initialUnion, parentId: union.id });
    }

    // Scroll smoothly to top of page
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, selectedUnionId]);

  const handleSave = (afterSave: string): Promise<boolean> => {
    return new Promise((resolve) => {
      if (selectedUnionId === "new" || selectedUnionId === "newLocal") {
        dispatch(
          createTradeUnion({
            ...union,
          }),
        ).then((res: any) => {
          if (afterSave === "continue") {
            // Make sure we are handling new union payload
            const newUnionPayload = res.payload.find(
              (payload: any) => payload.name === "trdUnionNew",
            );
            const newUnionId =
              newUnionPayload?.payload?.localTradeUnionId ?? newUnionPayload.id;

            // Update selectedUnion id to new union id
            setSelectedUnionId(newUnionId);
          }
          resolve(true);
        });
      } else {
        dispatch(
          updateTradeUnion({
            ...union,
            termsOfPayment: {
              ...union.termsOfPayment,
              payments: union.termsOfPayment.payments.map(
                (item: UnionPayment) => {
                  // Clone the current item
                  let updatedItem = { ...item };

                  // Normalize percentage if it exists
                  if (
                    item.percentage !== undefined &&
                    item.percentage !== null &&
                    item.percentage !== ""
                  ) {
                    updatedItem = {
                      ...updatedItem,
                      percentage: normalizeInputValue(
                        item.percentage.toString(),
                      ),
                    };
                  }

                  // Normalize fixedAmount if it exists
                  if (
                    item.fixedAmount !== undefined &&
                    item.fixedAmount !== null &&
                    item.fixedAmount !== ""
                  ) {
                    updatedItem = {
                      ...updatedItem,
                      fixedAmount: normalizeInputValue(
                        item.fixedAmount.toString(),
                      ),
                    };
                  }
                  return updatedItem;
                },
              ),
            },
          }),
        ).then((res: any) => {
          resolve(true);
        });
      }
    });
  };

  const titleNameSelector = (unionId: string) => {
    if (unionId === "new") {
      return t("settings:newUnion");
    } else if (unionId === "newLocal") {
      return t("settings:newLocalUnion");
    } else {
      return union.name;
    }
  };

  const saveDisabled = () => {
    // If name, tradeUnionNumer or acronym is empty, disable save button
    if (!union.name || !union.tradeUnionNumber || !union.acronym) {
      return true;
      // If union is same as stored union, disable save button
    } else if (areObjectsEqual(union, storedUnion || {})) {
      return true;
    } else {
      return false;
    }
  };

  const handleDeleteUnion = () => {
    // Check if union has parentId
    const parentId = union?.parentId;

    dispatch(
      deleteTradeUnions({
        tradeUnions: [
          {
            id: selectedUnionId,
            parentId: parentId || null,
          },
        ],
      }),
    ).then((res: any) => {
      // If response was successful TODO:

      setDeleteConfirmationDialogIsOpen(false);

      // If union has parentId, return should be to parent union instead of main listing
      parentId ? setSelectedUnionId(parentId) : setSelectedUnionId("");
    });
  };

  const handleSaveClick = async (afterSave: string) => {
    const isSuccess = await handleSave(afterSave);
    if (afterSave === "close" && isSuccess) {
      // If union has parentId, return should be to parent union instead of main listing
      union?.parentId
        ? setSelectedUnionId(union.parentId)
        : setSelectedUnionId("");
    }
  };

  return (
    <>
      <ConfirmationDialog
        title={t("settings:deleteUnionConfirmationTitle")}
        contentText={
          union?.parentId
            ? t("settings:deleteLocalUnionConfirmationContext")
            : t("settings:deleteUnionConfirmationContext")
        }
        confirmText={t("settings:deleteUnionButton")}
        cancelText={t("settings:deleteUnionCancelButton")}
        isOpen={deleteConfirmationDialogIsOpen}
        onCancel={() => {
          setDeleteConfirmationDialogIsOpen(false);
        }}
        onConfirm={() => {
          handleDeleteUnion();
        }}
      />
      <Box>
        <Box
          sx={{
            fontFamily: "Quicksand",
            marginLeft: "36px",
            color: "#4A1D9C",
            fontWeight: "700",
            fontSize: "14px",
          }}
        >
          {`${t("settings:unions").toUpperCase()} ${
            parentName && isLocalUnion ? " - " + parentName : ""
          }`}
        </Box>
        <Box
          sx={{
            fontFamily: "Quicksand",
            fontSize: "32px",
            fontWeight: 600,
            padding: "0px 0px 20px 0px",
            display: "flex",
            flexDirection: "row",
            alignItems: "center",
            width: "50%",
          }}
        >
          <img
            src={ArrowLeft}
            alt={"< Back: "}
            style={{ marginRight: "16px", cursor: "pointer", height: "32px" }}
            // If union has parentId, return should be to parent union instead of main listing
            onClick={() =>
              union?.parentId
                ? setSelectedUnionId(union.parentId)
                : setSelectedUnionId("")
            }
          />
          {titleNameSelector(selectedUnionId)}
        </Box>
      </Box>

      <UnionNameAndNumber
        union={union}
        setUnion={setUnion}
        selectedUnionId={selectedUnionId}
      />
      {selectedUnionId !== "new" && selectedUnionId !== "newLocal" && (
        <>
          <UnionFormBasicInfo union={union} setUnion={setUnion} />
          <UnionTermsOfPaymentList union={union} setUnion={setUnion} />

          <UnionBankAccount union={union} setUnion={setUnion} />
          {
            // Do not show local unions for local unions
            !isLocalUnion && (
              <UnionLocalTradeUnions
                union={union}
                setSelectedUnionId={setSelectedUnionId}
                handleSave={handleSave}
              />
            )
          }
          <UnionPaymentInfo union={union} setUnion={setUnion} />
        </>
      )}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          marginTop: "20px",
        }}
      >
        <Box>
          {selectedUnionId !== "new" && selectedUnionId !== "newLocal" && (
            <CustomButton
              variant="outlined"
              sx={{
                borderRadius: "15px",
                height: "30px",
                marginLeft: "20px",
                borderColor: "red",
              }}
              startIcon={<DeleteIcon sx={{ color: "red" }} />}
              onClick={() => setDeleteConfirmationDialogIsOpen(true)}
            >
              <Typography color="red" fontSize="14px">
                {t("settings:deleteUnion")}
              </Typography>
            </CustomButton>
          )}
        </Box>

        <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
          <CustomButton
            disabled={saveDisabled()}
            sx={{
              borderRadius: "15px",
              height: "30px",
              marginLeft: "20px",
            }}
            startIcon={<CheckIcon />}
            onClick={() => handleSaveClick("close")}
            variant="outlined"
          >
            {t("settings:saveAndClose")}
          </CustomButton>
          <CustomButton
            disabled={saveDisabled()}
            sx={{
              borderRadius: "15px",
              height: "30px",
              marginLeft: "20px",
            }}
            startIcon={<CheckIcon />}
            onClick={() => handleSaveClick("continue")}
            variant="contained"
          >
            {selectedUnionId === "new" || selectedUnionId === "newLocal"
              ? t("settings:saveAndContinue")
              : t("common:save")}
          </CustomButton>
        </Box>
      </Box>
    </>
  );
};

export default UnionView;
